import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiActivity, FiZap, FiGrid, FiHexagon, FiRotateCw, FiEye, FiTarget } = FiIcons;

const HolographicCategoryChart = ({ data = [], title = "Habit Category Distribution" }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [animatedData, setAnimatedData] = useState([]);

  // DreamForge theme colors with enhanced glow
  const CHART_COLORS = [
    { hex: '#FED40B', rgb: '254,212,11', name: 'Forge Gold' },
    { hex: '#1E293B', rgb: '30,41,59', name: 'Shadow Blue' },
    { hex: '#22c55e', rgb: '34,197,94', name: 'Success Green' },
    { hex: '#8b5cf6', rgb: '139,92,246', name: 'Vision Purple' },
    { hex: '#f59e0b', rgb: '245,158,11', name: 'Warning Amber' },
    { hex: '#ef4444', rgb: '239,68,68', name: 'Danger Red' },
    { hex: '#06b6d4', rgb: '6,182,212', name: 'Info Cyan' },
  ];

  // Process data for animation
  useEffect(() => {
    if (data.length > 0) {
      const initialData = data.map((item, index) => ({
        ...item,
        animatedCount: 0,
        color: CHART_COLORS[index % CHART_COLORS.length],
        glowIntensity: 0,
        rotationOffset: index * (360 / data.length),
      }));

      setAnimatedData(initialData);

      // Stagger animation for dramatic effect
      data.forEach((_, index) => {
        setTimeout(() => {
          setAnimatedData(prevData =>
            prevData.map((item, i) =>
              i === index ? {
                ...item,
                animatedCount: item.count,
                glowIntensity: 1
              } : item
            )
          );
        }, index * 300);
      });
    }
  }, [data]);

  // Enhanced DreamForge-style tooltip
  const DreamForgeTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-xl p-4 rounded-2xl border border-forge-400/50 shadow-dominance"
          style={{
            boxShadow: `
              0 0 20px rgba(254,212,11,0.4),
              inset 0 0 15px rgba(254,212,11,0.1)
            `
          }}
        >
          {/* DreamForge corner brackets */}
          <div className="absolute top-1 left-1 w-3 h-3 border-t border-l border-forge-400 opacity-80"></div>
          <div className="absolute top-1 right-1 w-3 h-3 border-t border-r border-forge-400 opacity-80"></div>
          <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-forge-400 opacity-80"></div>
          <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-forge-400 opacity-80"></div>

          <div className="flex items-center space-x-2 mb-3">
            <div
              className="w-4 h-4 rounded border border-forge-400 animate-pulse"
              style={{
                backgroundColor: data.color.hex,
                boxShadow: `0 0 10px rgba(${data.color.rgb},0.6)`
              }}
            ></div>
            <p className="font-bold text-forge-400 tracking-wide">
              {data.category.toUpperCase()}
            </p>
          </div>

          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;COUNT:</span>
              <span className="text-white font-bold">{data.count}</span>
            </div>
            {data.completion && (
              <div className="flex justify-between items-center text-forge-300">
                <span className="text-forge-400">&gt;RATE:</span>
                <span className="text-white font-bold">{data.completion}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;STATUS:</span>
              <span className="text-forge-400 font-bold animate-pulse">ACTIVE</span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  // Interactive controls
  const toggleRotation = () => {
    setIsRotating(!isRotating);
  };

  const chartData = animatedData.length > 0 ? animatedData : [];

  return (
    <div className="relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl border border-forge-400/30 shadow-power overflow-hidden">
      {/* Container with proper responsive padding */}
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Header with DreamForge styling - Responsive */}
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center space-x-3">
            <motion.div
              className="p-2 sm:p-3 bg-forge-gradient rounded-2xl shadow-lg"
              animate={{
                boxShadow: [
                  "0 0 15px rgba(254,212,11,0.3)",
                  "0 0 25px rgba(254,212,11,0.5)",
                  "0 0 15px rgba(254,212,11,0.3)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <SafeIcon icon={FiTarget} className="text-shadow-950 text-lg sm:text-xl" />
            </motion.div>
            <div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-forge-400 tracking-wide">
                {title}
              </h3>
              <p className="text-forge-300 text-xs sm:text-sm opacity-80">
                Category distribution analysis
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <motion.button
              onClick={toggleRotation}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 sm:p-3 bg-shadow-800/50 text-forge-400 border border-forge-400/50 hover:border-forge-400 transition-all duration-300 rounded-xl text-sm"
              style={{
                boxShadow: isRotating
                  ? "0 0 15px rgba(254,212,11,0.5)"
                  : "0 0 8px rgba(254,212,11,0.2)"
              }}
            >
              <SafeIcon icon={FiRotateCw} className={`text-base sm:text-lg ${isRotating ? "animate-spin" : ""}`} />
            </motion.button>
          </div>
        </div>

        {/* Enhanced chart container - Responsive heights */}
        <div className="relative z-10 h-[300px] sm:h-[350px] lg:h-[400px] mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {CHART_COLORS.map((colorData, index) => (
                  <React.Fragment key={`gradient-${index}`}>
                    <radialGradient
                      id={`chart-gradient-${index}`}
                      cx="50%"
                      cy="50%"
                      r="80%"
                    >
                      <stop offset="0%" stopColor={colorData.hex} stopOpacity={0.9} />
                      <stop offset="40%" stopColor={colorData.hex} stopOpacity={0.6} />
                      <stop offset="80%" stopColor={colorData.hex} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={colorData.hex} stopOpacity={0.1} />
                    </radialGradient>
                    <filter
                      id={`chart-glow-${index}`}
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </React.Fragment>
                ))}
              </defs>
              <Pie
                data={chartData}
                dataKey="animatedCount"
                nameKey="category"
                cx="50%"
                cy="50%"
                outerRadius={window.innerWidth >= 1024 ? 140 : window.innerWidth >= 640 ? 110 : 90}
                innerRadius={window.innerWidth >= 1024 ? 70 : window.innerWidth >= 640 ? 55 : 45}
                paddingAngle={4}
                animationDuration={2500}
                animationBegin={0}
                startAngle={isRotating ? 0 : 90}
                endAngle={isRotating ? 360 : 450}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#chart-gradient-${index % CHART_COLORS.length})`}
                    stroke={entry.color.hex}
                    strokeWidth={hoveredIndex === index ? 4 : 2}
                    filter={hoveredIndex === index ? `url(#chart-glow-${index % CHART_COLORS.length})` : 'none'}
                    style={{
                      transform: hoveredIndex === index ? 'scale(1.05)' : 'scale(1)',
                      transformOrigin: 'center',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: hoveredIndex === index ? 'brightness(1.3) saturate(1.1)' : 'brightness(1)',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<DreamForgeTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* DreamForge-style legend - Responsive grid */}
        <div className="relative z-10 flex flex-wrap justify-center gap-2 sm:gap-3">
          {chartData.map((category, index) => (
            <motion.div
              key={`legend-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + index * 0.1 }}
              className="flex items-center space-x-2 bg-shadow-800/50 backdrop-blur-xl px-2 sm:px-3 py-1 sm:py-2 border border-forge-400/30 hover:border-forge-400/60 transition-all duration-300 rounded-xl text-xs sm:text-sm"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                boxShadow: hoveredIndex === index
                  ? `0 0 20px rgba(${category.color.rgb},0.5), 0 0 30px rgba(254,212,11,0.3)`
                  : `0 0 8px rgba(${category.color.rgb},0.2)`
              }}
            >
              <motion.div
                className="w-3 h-3 border border-forge-400 rounded-sm"
                style={{ backgroundColor: category.color.hex }}
                animate={{
                  boxShadow: [
                    `0 0 3px rgba(${category.color.rgb},0.5)`,
                    `0 0 8px rgba(${category.color.rgb},0.8)`,
                    `0 0 3px rgba(${category.color.rgb},0.5)`
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-forge-300 tracking-wide uppercase truncate max-w-20 sm:max-w-none">
                {category.category}
              </span>
              <span className="text-forge-400 font-bold">
                [{category.animatedCount}]
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* DreamForge border effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-forge-400 to-transparent opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-forge-400 to-transparent opacity-30"></div>
        <div className="absolute top-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-forge-400 to-transparent opacity-30"></div>
        <div className="absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-transparent via-forge-400 to-transparent opacity-30"></div>
      </div>
    </div>
  );
};

export default HolographicCategoryChart;